import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Package, Scan, Search, Loader2, Camera, X, 
  Plus, Minus, ShoppingCart, Check, AlertTriangle,
  Box, ClipboardList, Send, QrCode
} from 'lucide-react';

export default function CaregiverSupplyPortal({ shiftId, clientId }) {
  const [activeTab, setActiveTab] = useState('inventory');
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [myRequests, setMyRequests] = useState([]);
  
  // Scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanQuantity, setScanQuantity] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const [usageNotes, setUsageNotes] = useState('');
  
  // Request form state
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestItem, setRequestItem] = useState(null);
  const [requestQuantity, setRequestQuantity] = useState(1);
  const [requestNotes, setRequestNotes] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    loadInventory();
    loadMyRequests();
  }, []);

  const loadInventory = async () => {
    try {
      const response = await api.get('/supplies/inventory/available');
      setInventory(response.data.inventory || []);
    } catch (error) {
      console.error('Error loading inventory:', error);
      toast.error('Failed to load supplies');
    } finally {
      setLoading(false);
    }
  };

  const loadMyRequests = async () => {
    try {
      const response = await api.get('/supplies/requests/my');
      setMyRequests(response.data.requests || []);
    } catch (error) {
      console.error('Error loading requests:', error);
    }
  };

  // Barcode scanning
  const startScanner = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setScannerOpen(true);
      toast.info('Point camera at barcode');
    } catch (error) {
      toast.error('Could not access camera');
      console.error('Camera access error:', error);
    }
  };

  const stopScanner = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScannerOpen(false);
    setScanResult(null);
  };

  const handleManualBarcodeSubmit = async () => {
    if (!manualBarcode.trim()) {
      toast.error('Please enter a barcode');
      return;
    }
    await lookupBarcode(manualBarcode.trim());
  };

  const lookupBarcode = async (barcode) => {
    setProcessing(true);
    try {
      const response = await api.get(`/supplies/lookup/${barcode}`);
      if (response.data.supply) {
        setScanResult(response.data);
        toast.success(`Found: ${response.data.supply.name}`);
      } else {
        toast.error('Supply not found');
      }
    } catch (error) {
      toast.error('Supply not found in system');
    } finally {
      setProcessing(false);
    }
  };

  const handleLogUsage = async () => {
    if (!scanResult || scanQuantity < 1) return;
    
    setProcessing(true);
    try {
      await api.post('/supplies/inventory/usage', {
        supply_id: scanResult.supply.id,
        quantity: scanQuantity,
        shift_id: shiftId,
        client_id: clientId,
        notes: usageNotes
      });
      toast.success('Supply usage logged');
      setScanResult(null);
      setScanQuantity(1);
      setUsageNotes('');
      setManualBarcode('');
      loadInventory();
    } catch (error) {
      toast.error('Failed to log usage');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!requestItem || requestQuantity < 1) return;
    
    setSubmittingRequest(true);
    try {
      await api.post('/supplies/requests', {
        supply_id: requestItem.id,
        quantity: requestQuantity,
        notes: requestNotes,
        client_id: clientId
      });
      toast.success('Supply request submitted');
      setShowRequestForm(false);
      setRequestItem(null);
      setRequestQuantity(1);
      setRequestNotes('');
      loadMyRequests();
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const filteredInventory = inventory.filter(item =>
    item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="icon-container">
            <Package className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-purple-900">Supply Portal</h3>
            <p className="text-xs text-gray-500">View inventory, scan, and request supplies</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-purple-100/60 pb-px">
        <button
          onClick={() => setActiveTab('inventory')}
          className={`pb-3 px-4 font-medium transition-all duration-200 relative text-sm ${
            activeTab === 'inventory' ? 'text-purple-900' : 'text-gray-500 hover:text-purple-700'
          }`}
        >
          <Box className="w-4 h-4 inline mr-2" />
          Inventory
          {activeTab === 'inventory' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
        </button>
        <button
          onClick={() => setActiveTab('scan')}
          className={`pb-3 px-4 font-medium transition-all duration-200 relative text-sm ${
            activeTab === 'scan' ? 'text-purple-900' : 'text-gray-500 hover:text-purple-700'
          }`}
        >
          <Scan className="w-4 h-4 inline mr-2" />
          Scan & Log
          {activeTab === 'scan' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
        </button>
        <button
          onClick={() => setActiveTab('requests')}
          className={`pb-3 px-4 font-medium transition-all duration-200 relative text-sm ${
            activeTab === 'requests' ? 'text-purple-900' : 'text-gray-500 hover:text-purple-700'
          }`}
        >
          <ShoppingCart className="w-4 h-4 inline mr-2" />
          My Requests
          {myRequests.filter(r => r.status === 'pending').length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
              {myRequests.filter(r => r.status === 'pending').length}
            </span>
          )}
          {activeTab === 'requests' && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-purple-600 rounded-full" />}
        </button>
      </div>

      {/* Inventory Tab */}
      {activeTab === 'inventory' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search supplies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Inventory Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : filteredInventory.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No supplies found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filteredInventory.map((item) => (
                <div key={item.id} className="card-premium p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-purple-900">{item.name}</h4>
                      <p className="text-xs text-gray-500">{item.category}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className={`text-sm font-semibold ${
                          item.quantity <= item.reorder_threshold ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {item.quantity} in stock
                        </span>
                        {item.quantity <= item.reorder_threshold && (
                          <span className="text-xs text-amber-600 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Low
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setRequestItem(item);
                        setShowRequestForm(true);
                      }}
                      className="text-purple-600 border-purple-200 hover:bg-purple-50"
                    >
                      Request
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Scan & Log Tab */}
      {activeTab === 'scan' && (
        <div className="space-y-4">
          {/* Scanner Area */}
          <div className="card-premium p-6">
            <h4 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
              <QrCode className="w-5 h-5 text-purple-600" />
              Scan Supply Barcode
            </h4>

            {scannerOpen ? (
              <div className="space-y-4">
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-64 h-32 border-2 border-white/50 rounded-lg"></div>
                  </div>
                </div>
                <Button onClick={stopScanner} variant="outline" className="w-full">
                  <X className="w-4 h-4 mr-2" />
                  Close Scanner
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <Button onClick={startScanner} className="w-full btn-primary">
                  <Camera className="w-4 h-4 mr-2" />
                  Open Camera Scanner
                </Button>
                
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <span className="text-xs text-gray-400">or enter manually</span>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>

                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Enter barcode/SKU"
                    value={manualBarcode}
                    onChange={(e) => setManualBarcode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleManualBarcodeSubmit()}
                  />
                  <Button onClick={handleManualBarcodeSubmit} disabled={processing}>
                    {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Scan Result */}
          {scanResult && (
            <div className="card-premium p-6 border-green-200 bg-green-50">
              <div className="flex items-center gap-2 text-green-700 mb-4">
                <Check className="w-5 h-5" />
                <span className="font-semibold">Supply Found</span>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-purple-900">{scanResult.supply.name}</h4>
                  <p className="text-sm text-gray-500">{scanResult.supply.category}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Current stock: <span className="font-medium">{scanResult.inventory?.quantity || 0}</span>
                  </p>
                </div>

                <div>
                  <Label className="text-sm">Quantity Used</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setScanQuantity(Math.max(1, scanQuantity - 1))}
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-xl font-bold text-purple-900 w-12 text-center">
                      {scanQuantity}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setScanQuantity(scanQuantity + 1)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-sm">Notes (optional)</Label>
                  <Textarea
                    placeholder="e.g., Used for wound dressing change"
                    value={usageNotes}
                    onChange={(e) => setUsageNotes(e.target.value)}
                    rows={2}
                    className="mt-1"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleLogUsage}
                    disabled={processing}
                    className="flex-1 btn-primary"
                  >
                    {processing ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <ClipboardList className="w-4 h-4 mr-2" />
                    )}
                    Log Usage
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setScanResult(null);
                      setManualBarcode('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* My Requests Tab */}
      {activeTab === 'requests' && (
        <div className="space-y-4">
          {myRequests.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No supply requests</p>
              <p className="text-xs text-gray-400 mt-1">Request supplies from the Inventory tab</p>
            </div>
          ) : (
            <div className="space-y-3">
              {myRequests.map((request) => (
                <div key={request.id} className="card-premium p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-purple-900">{request.supply_name}</h4>
                      <p className="text-sm text-gray-500">Qty: {request.quantity}</p>
                      {request.notes && (
                        <p className="text-xs text-gray-400 mt-1">{request.notes}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Requested: {formatDate(request.created_at)}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                      request.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      request.status === 'approved' ? 'bg-green-100 text-green-700' :
                      request.status === 'fulfilled' ? 'bg-blue-100 text-blue-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {request.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Request Form Modal */}
      {showRequestForm && requestItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-purple-900 mb-4">Request Supply</h3>
            
            <div className="space-y-4">
              <div className="bg-purple-50 rounded-lg p-3">
                <h4 className="font-medium text-purple-900">{requestItem.name}</h4>
                <p className="text-sm text-gray-500">{requestItem.category}</p>
              </div>

              <div>
                <Label>Quantity Needed</Label>
                <div className="flex items-center gap-3 mt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRequestQuantity(Math.max(1, requestQuantity - 1))}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="text-xl font-bold text-purple-900 w-12 text-center">
                    {requestQuantity}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRequestQuantity(requestQuantity + 1)}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Why do you need this supply?"
                  value={requestNotes}
                  onChange={(e) => setRequestNotes(e.target.value)}
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleSubmitRequest}
                  disabled={submittingRequest}
                  className="flex-1 btn-primary"
                >
                  {submittingRequest ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Submit Request
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRequestForm(false);
                    setRequestItem(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
